import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import PageHeader from '../../components/ui/PageHeader';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const Settings = () => {
  const { userProfile, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('notifications');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [notificationSettings, setNotificationSettings] = useState({
    emailTaskAssigned: true,
    emailTaskCompleted: false,
    emailSprintStarted: true,
    emailSprintCompleted: true,
    emailMentions: true,
    emailWeeklyDigest: true,
    pushTaskAssigned: true,
    pushMentions: true,
    pushDeadlineReminders: true,
  });

  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: 'light',
    density: 'comfortable',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
  });

  const [workspaceSettings, setWorkspaceSettings] = useState({
    defaultView: 'kanban',
    autoAssign: false,
    showCompletedTasks: true,
    taskSortBy: 'priority',
  });

  const handleNotificationToggle = (key) => {
    setNotificationSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateProfile({
        notification_preferences: notificationSettings
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'appearance', label: 'Appearance', icon: 'Palette' },
    { id: 'workspace', label: 'Workspace', icon: 'Layout' },
    { id: 'integrations', label: 'Integrations', icon: 'Plug' },
    { id: 'security', label: 'Security', icon: 'Shield' },
  ];

  const ToggleSwitch = ({ checked, onChange, label, description }) => (
    <div className="flex items-center justify-between py-4 border-b border-border last:border-0">
      <div>
        <p className="text-sm font-medium text-text-primary">{label}</p>
        {description && <p className="text-xs text-text-secondary mt-0.5">{description}</p>}
      </div>
      <button onClick={onChange}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
          checked ? 'bg-primary' : 'bg-secondary-300'
        }`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`} />
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />
      <main className="lg:ml-60 pt-16">
        <div className="p-6">
          <PageHeader
            title="Settings"
            description="Manage your account settings and preferences"
            actions={
              <button onClick={handleSave} disabled={isSaving}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50">
                {isSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Save" size={16} />}
                <span>{isSaving ? 'Saving...' : 'Save Settings'}</span>
              </button>
            }
          />

          {saveSuccess && (
            <div className="mb-6 p-4 bg-success-50 border border-success-100 rounded-lg flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} color="var(--color-success)" />
              <p className="text-sm text-success">Settings saved successfully!</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Tabs Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-surface rounded-xl border border-border p-2">
                {tabs?.map((tab) => (
                  <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 text-left ${
                      activeTab === tab?.id ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary hover:bg-secondary-50'
                    }`}>
                    <Icon name={tab?.icon} size={18} color={activeTab === tab?.id ? 'white' : 'currentColor'} />
                    <span className="text-sm font-medium">{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Settings Content */}
            <div className="lg:col-span-3">
              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <div className="bg-surface rounded-xl border border-border p-6">
                    <h3 className="text-lg font-semibold text-text-primary mb-2">Email Notifications</h3>
                    <p className="text-sm text-text-secondary mb-6">Choose which email notifications you want to receive.</p>
                    <ToggleSwitch checked={notificationSettings?.emailTaskAssigned} onChange={() => handleNotificationToggle('emailTaskAssigned')}
                      label="Task Assigned" description="When a task is assigned to you" />
                    <ToggleSwitch checked={notificationSettings?.emailTaskCompleted} onChange={() => handleNotificationToggle('emailTaskCompleted')}
                      label="Task Completed" description="When tasks you created are completed" />
                    <ToggleSwitch checked={notificationSettings?.emailSprintStarted} onChange={() => handleNotificationToggle('emailSprintStarted')}
                      label="Sprint Started" description="When a new sprint begins" />
                    <ToggleSwitch checked={notificationSettings?.emailSprintCompleted} onChange={() => handleNotificationToggle('emailSprintCompleted')}
                      label="Sprint Completed" description="When a sprint is completed" />
                    <ToggleSwitch checked={notificationSettings?.emailMentions} onChange={() => handleNotificationToggle('emailMentions')}
                      label="Mentions" description="When someone mentions you in a comment" />
                    <ToggleSwitch checked={notificationSettings?.emailWeeklyDigest} onChange={() => handleNotificationToggle('emailWeeklyDigest')}
                      label="Weekly Digest" description="Weekly summary of your team's activity" />
                  </div>
                  <div className="bg-surface rounded-xl border border-border p-6">
                    <h3 className="text-lg font-semibold text-text-primary mb-2">Push Notifications</h3>
                    <p className="text-sm text-text-secondary mb-6">Manage in-app push notification preferences.</p>
                    <ToggleSwitch checked={notificationSettings?.pushTaskAssigned} onChange={() => handleNotificationToggle('pushTaskAssigned')}
                      label="Task Assigned" description="Instant notification when assigned a task" />
                    <ToggleSwitch checked={notificationSettings?.pushMentions} onChange={() => handleNotificationToggle('pushMentions')}
                      label="Mentions" description="When someone mentions you" />
                    <ToggleSwitch checked={notificationSettings?.pushDeadlineReminders} onChange={() => handleNotificationToggle('pushDeadlineReminders')}
                      label="Deadline Reminders" description="Reminders before task due dates" />
                  </div>
                </div>
              )}

              {activeTab === 'appearance' && (
                <div className="bg-surface rounded-xl border border-border p-6">
                  <h3 className="text-lg font-semibold text-text-primary mb-6">Appearance Settings</h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-3">Theme</label>
                      <div className="grid grid-cols-3 gap-3">
                        {['light', 'dark', 'system']?.map((theme) => (
                          <button key={theme} onClick={() => setAppearanceSettings(prev => ({ ...prev, theme }))}
                            className={`p-4 rounded-lg border-2 transition-colors duration-200 capitalize ${
                              appearanceSettings?.theme === theme ? 'border-primary bg-primary-50 text-primary' : 'border-border hover:border-primary-300'
                            }`}>
                            <Icon name={theme === 'light' ? 'Sun' : theme === 'dark' ? 'Moon' : 'Monitor'} size={20} className="mx-auto mb-2" />
                            <p className="text-sm font-medium">{theme}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">Display Density</label>
                      <select value={appearanceSettings?.density} onChange={(e) => setAppearanceSettings(prev => ({ ...prev, density: e?.target?.value }))}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="compact">Compact</option>
                        <option value="comfortable">Comfortable</option>
                        <option value="spacious">Spacious</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">Language</label>
                      <select value={appearanceSettings?.language} onChange={(e) => setAppearanceSettings(prev => ({ ...prev, language: e?.target?.value }))}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                        <option value="ja">Japanese</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">Date Format</label>
                        <select value={appearanceSettings?.dateFormat} onChange={(e) => setAppearanceSettings(prev => ({ ...prev, dateFormat: e?.target?.value }))}
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">Time Format</label>
                        <select value={appearanceSettings?.timeFormat} onChange={(e) => setAppearanceSettings(prev => ({ ...prev, timeFormat: e?.target?.value }))}
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                          <option value="12h">12-hour</option>
                          <option value="24h">24-hour</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'workspace' && (
                <div className="bg-surface rounded-xl border border-border p-6">
                  <h3 className="text-lg font-semibold text-text-primary mb-6">Workspace Preferences</h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">Default View</label>
                      <select value={workspaceSettings?.defaultView} onChange={(e) => setWorkspaceSettings(prev => ({ ...prev, defaultView: e?.target?.value }))}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="kanban">Kanban Board</option>
                        <option value="list">List View</option>
                        <option value="calendar">Calendar View</option>
                        <option value="dashboard">Dashboard</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">Sort Tasks By</label>
                      <select value={workspaceSettings?.taskSortBy} onChange={(e) => setWorkspaceSettings(prev => ({ ...prev, taskSortBy: e?.target?.value }))}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="priority">Priority</option>
                        <option value="due_date">Due Date</option>
                        <option value="created_at">Created Date</option>
                        <option value="title">Title</option>
                      </select>
                    </div>
                    <ToggleSwitch checked={workspaceSettings?.autoAssign} onChange={() => setWorkspaceSettings(prev => ({ ...prev, autoAssign: !prev?.autoAssign }))}
                      label="Auto-assign Tasks" description="Automatically assign tasks to yourself when creating" />
                    <ToggleSwitch checked={workspaceSettings?.showCompletedTasks} onChange={() => setWorkspaceSettings(prev => ({ ...prev, showCompletedTasks: !prev?.showCompletedTasks }))}
                      label="Show Completed Tasks" description="Display completed tasks in board views" />
                  </div>
                </div>
              )}

              {activeTab === 'integrations' && (
                <div className="bg-surface rounded-xl border border-border p-6">
                  <h3 className="text-lg font-semibold text-text-primary mb-6">Integrations</h3>
                  <div className="space-y-4">
                    {[
                      { name: 'GitHub', icon: 'Github', desc: 'Connect your GitHub repositories to link commits and PRs to tasks', connected: false },
                      { name: 'Slack', icon: 'MessageSquare', desc: 'Get Arkdai notifications directly in your Slack workspace', connected: false },
                      { name: 'Jira', icon: 'ExternalLink', desc: 'Import and sync issues from Jira projects', connected: false },
                      { name: 'Figma', icon: 'Figma', desc: 'Embed Figma designs directly in task descriptions', connected: false },
                    ]?.map((integration) => (
                      <div key={integration?.name} className="flex items-center justify-between p-4 border border-border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
                            <Icon name={integration?.icon} size={20} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-text-primary">{integration?.name}</p>
                            <p className="text-xs text-text-secondary">{integration?.desc}</p>
                          </div>
                        </div>
                        <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                          integration?.connected
                            ? 'bg-success-50 text-success hover:bg-success-100' :'bg-primary text-white hover:bg-primary-700'
                        }`}>
                          {integration?.connected ? 'Connected' : 'Connect'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div className="bg-surface rounded-xl border border-border p-6">
                    <h3 className="text-lg font-semibold text-text-primary mb-6">Security Settings</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-text-primary">Two-Factor Authentication</p>
                          <p className="text-xs text-text-secondary">Add an extra layer of security to your account</p>
                        </div>
                        <button className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary-700 transition-colors duration-200">Enable 2FA</button>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-text-primary">Active Sessions</p>
                          <p className="text-xs text-text-secondary">Manage devices where you are logged in</p>
                        </div>
                        <button className="px-4 py-2 bg-secondary-100 text-secondary-700 rounded-lg text-sm hover:bg-secondary-200 transition-colors duration-200">View Sessions</button>
                      </div>
                    </div>
                  </div>
                  <div className="bg-surface rounded-xl border border-error-100 p-6">
                    <h3 className="text-lg font-semibold text-error mb-2">Danger Zone</h3>
                    <p className="text-sm text-text-secondary mb-4">These actions are irreversible. Please proceed with caution.</p>
                    <div className="space-y-3">
                      <button className="w-full px-4 py-3 border border-error text-error rounded-lg hover:bg-error-50 transition-colors duration-200 text-sm font-medium text-left">
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
